import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Save, ExternalLink, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const slugify = (s) =>
  (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();

export default function InvitesManager() {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", max_guests: 1 });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invites");
      setInvites(data);
    } catch {
      toast.error("No se pudieron cargar las invitaciones");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Escribe el nombre del invitado");
    setCreating(true);
    try {
      await api.post("/invites", { name: form.name, slug: form.slug || null, max_guests: Number(form.max_guests) || 1 });
      setForm({ name: "", slug: "", max_guests: 1 });
      toast.success("Invitación creada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const linkFor = (slug) => `${window.location.origin}/invitacion/${slug}`;

  return (
    <div className="pb-16" data-testid="invites-manager">
      {/* Create */}
      <div className="border border-black/10 p-6 sm:p-8 mb-10">
        <h3 className="font-display text-2xl mb-6">Nueva invitación</h3>
        <div className="grid sm:grid-cols-[1.4fr_1.4fr_0.6fr_auto] gap-4 items-end">
          <div>
            <label className="overline text-[color:var(--muted)] block mb-2">Nombre (invitado o grupo)</label>
            <input className="field-underline" placeholder="Ej. Familia López / Juan +1"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) }))}
              data-testid="invite-form-name" />
          </div>
          <div>
            <label className="overline text-[color:var(--muted)] block mb-2">Slug (link)</label>
            <input className="field-underline" placeholder="familia-lopez"
              value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
              data-testid="invite-form-slug" />
          </div>
          <div>
            <label className="overline text-[color:var(--muted)] block mb-2">Pases</label>
            <input type="number" min="1" className="field-underline" value={form.max_guests}
              onChange={(e) => setForm((f) => ({ ...f, max_guests: e.target.value }))} data-testid="invite-form-max" />
          </div>
          <button onClick={create} disabled={creating}
            className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 border border-ink hover:bg-white hover:text-ink transition-colors duration-300 overline disabled:opacity-50"
            data-testid="invite-form-create">
            <Plus size={15} /> Crear
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-4" data-testid="invites-list">
        {invites.length === 0 && <p className="font-light text-[color:var(--muted)]">Aún no hay invitaciones. Crea la primera arriba.</p>}
        {invites.map((inv) => (
          <InviteRow key={inv.id} inv={inv} link={linkFor(inv.slug)} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function InviteRow({ inv, link, onChanged }) {
  const [name, setName] = useState(inv.name);
  const [slug, setSlug] = useState(inv.slug);
  const [max, setMax] = useState(inv.max_guests);
  const [saving, setSaving] = useState(false);

  const rsvp = inv.rsvp;
  const status = !rsvp ? { l: "Pendiente", c: "border border-black/20 text-[color:var(--muted)]" }
    : rsvp.attending ? { l: `Asiste · ${rsvp.guests}`, c: "bg-ink text-white" }
    : { l: "No asiste", c: "border border-black/30 text-ink" };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/invites/${inv.id}`, { name, slug, max_guests: Number(max) || 1 });
      toast.success("Guardado");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const del = async () => {
    if (!window.confirm(`¿Eliminar la invitación de "${inv.name}"?`)) return;
    try { await api.delete(`/invites/${inv.id}`); toast.success("Eliminada"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Link copiado"); };
  const toggleSent = async () => {
    try {
      await api.put(`/invites/${inv.id}`, { sent: !inv.sent });
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="border border-black/10 p-5" data-testid={`invite-row-${inv.slug}`}>
      <div className="grid lg:grid-cols-[1.4fr_1.4fr_0.5fr_auto] gap-4 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Nombre</label>
          <input className="field-underline" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Slug</label>
          <input className="field-underline" value={slug} onChange={(e) => setSlug(e.target.value)} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Pases</label>
          <input type="number" min="1" className="field-underline" value={max} onChange={(e) => setMax(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving} className="w-10 h-10 border border-black/15 flex items-center justify-center hover:bg-ink hover:text-white transition-colors" title="Guardar"><Save size={15} /></button>
          <button onClick={del} className="w-10 h-10 border border-black/15 flex items-center justify-center hover:bg-destructive hover:text-white hover:border-destructive transition-colors" title="Eliminar"><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={toggleSent} className="inline-flex items-center gap-2 text-xs text-[color:var(--muted)] hover:text-ink transition-colors shrink-0" data-testid={`invite-sent-${inv.slug}`} title="Marcar invitación como enviada">
            <span className={`w-4 h-4 border flex items-center justify-center transition-colors ${inv.sent ? "bg-ink border-ink text-white" : "border-black/25"}`}>
              {inv.sent && <Check size={11} strokeWidth={3} />}
            </span>
            Enviada
          </button>
          <span className={`text-xs px-3 py-1 shrink-0 ${status.c}`}>{status.l}</span>
          <span className="text-sm font-light text-[color:var(--muted)] truncate">{link}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={copy} className="inline-flex items-center gap-2 border border-black/15 px-4 py-2 hover:bg-ink hover:text-white transition-colors overline" data-testid={`invite-copy-${inv.slug}`}><Copy size={13} /> Copiar</button>
          <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-black/15 px-4 py-2 hover:bg-ink hover:text-white transition-colors overline"><ExternalLink size={13} /> Abrir</a>
        </div>
      </div>
    </div>
  );
}
